import { useState } from 'react';
import {
  Badge,
  Body1,
  Body1Strong,
  Button,
  Caption1,
  Divider,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  OverlayDrawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Subtitle2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AddRegular, Dismiss24Regular } from '@fluentui/react-icons';

import { TAssessmentTemplateConfig, TEMPLATE_FIELD_TYPE, TTemplateFieldType } from '../../models';
import { TAddCustomFieldInput } from '../../state/ChangeV2Store';
import { AddCustomFieldDialog } from './AddCustomFieldDialog';

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, paddingTop: tokens.spacingVerticalM },
  block: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  chips: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  sectionRow: { display: 'flex', flexDirection: 'column', gap: '2px' },
  muted: { color: tokens.colorNeutralForeground3 },
  configHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
  },
});

// The MVP control set offered when adding a custom field (prompt Part 3).
const FIELD_TYPES: { type: TTemplateFieldType; label: string }[] = [
  { type: TEMPLATE_FIELD_TYPE.SHORT_TEXT, label: 'Short text' },
  { type: TEMPLATE_FIELD_TYPE.LONG_TEXT, label: 'Long text' },
  { type: TEMPLATE_FIELD_TYPE.YES_NO_UNKNOWN, label: 'Yes / No / Unknown' },
  { type: TEMPLATE_FIELD_TYPE.SINGLE_CHOICE, label: 'Single choice' },
  { type: TEMPLATE_FIELD_TYPE.MULTI_SELECT, label: 'Multi-select' },
  { type: TEMPLATE_FIELD_TYPE.DATE, label: 'Date' },
  { type: TEMPLATE_FIELD_TYPE.PERSON, label: 'Person or function' },
  { type: TEMPLATE_FIELD_TYPE.ENTITY_REFERENCE, label: 'Product or document reference' },
  { type: TEMPLATE_FIELD_TYPE.EVIDENCE_REFERENCE, label: 'Attachment or evidence reference' },
];

type TProps = {
  template?: TAssessmentTemplateConfig;
  open: boolean;
  onClose: () => void;
  /** Show the administrator `Add custom field` affordance. */
  admin?: boolean;
  onAddCustomField?: (input: TAddCustomFieldInput) => void;
};

// A read-only view of the assessment template, reused by triage (read-only) and
// the assessment workspace (with the lightweight `Add custom field` prototype).
export const TemplateDetailsDrawer = ({ template, open, onClose, admin = false, onAddCustomField }: TProps) => {
  const classes = useStyles();
  const [pendingType, setPendingType] = useState<{ type: TTemplateFieldType; label: string } | undefined>();

  if (!template) return null;

  const conditionSources = [
    ...template.customFields.map((field) => ({ key: field.id, label: field.label })),
    ...template.assessmentSections.flatMap((section) =>
      section.questions.map((question) => ({ key: question.id, label: question.label }))
    ),
  ];

  const handleAdd = (input: TAddCustomFieldInput) => {
    onAddCustomField?.(input);
    setPendingType(undefined);
  };

  return (
    <>
      <OverlayDrawer
        open={open}
        onOpenChange={(_, data) => !data.open && onClose()}
        position="end"
        style={{ width: '480px' }}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={<Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={onClose} />}
          >
            Template details
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>
          <div className={classes.body}>
            <div className={classes.block}>
              <Subtitle2>{template.name}</Subtitle2>
              <Caption1 className={classes.muted}>Version {template.version}</Caption1>
              <Body1>{template.description}</Body1>
            </div>

            <div className={classes.block}>
              <Body1Strong>Assessment sections</Body1Strong>
              {template.assessmentSections.map((section) => (
                <div key={section.id} className={classes.sectionRow}>
                  <Body1>{section.title}</Body1>
                  <Caption1 className={classes.muted}>{section.responsibleFunction}</Caption1>
                </div>
              ))}
            </div>

            <div className={classes.block}>
              <Body1Strong>Suggested assessor functions</Body1Strong>
              <div className={classes.chips}>
                {template.suggestedAssessorFunctions.map((assessorFunction) => (
                  <Badge key={assessorFunction} appearance="outline" color="informative">
                    {assessorFunction}
                  </Badge>
                ))}
              </div>
            </div>

            <Divider />

            <div className={classes.block}>
              <div className={classes.configHeader}>
                <div>
                  <Body1Strong>Template custom fields</Body1Strong>
                  <Caption1 className={classes.muted} block>
                    {template.customFields.length} configured field{template.customFields.length === 1 ? '' : 's'}
                  </Caption1>
                </div>
                {admin ? (
                  <Menu>
                    <MenuTrigger disableButtonEnhancement>
                      <Button appearance="secondary" icon={<AddRegular />}>
                        Add custom field
                      </Button>
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        {FIELD_TYPES.map((entry) => (
                          <MenuItem key={entry.type} onClick={() => setPendingType(entry)}>
                            {entry.label}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                ) : null}
              </div>
              {admin ? (
                <Caption1 className={classes.muted}>
                  For an organization administrator or template manager. Template changes apply to newly created
                  Changes.
                </Caption1>
              ) : null}
            </div>
          </div>
        </DrawerBody>
      </OverlayDrawer>

      <AddCustomFieldDialog
        open={Boolean(pendingType)}
        fieldType={pendingType?.type}
        fieldTypeLabel={pendingType?.label}
        sections={template.assessmentSections.map((section) => ({ id: section.id, title: section.title }))}
        conditionSources={conditionSources}
        onClose={() => setPendingType(undefined)}
        onAdd={handleAdd}
      />
    </>
  );
};
